import { Typography, Stack, Skeleton } from "@mui/material";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useTenant } from "hooks/settings";
import { useGetInventoryEstimateAtTimestampQuery } from "src/store/api/generatedApi";
import { useFormatNumber } from "src/utils/formatNumber";
import { CurrentInventoryPieChart } from "./CurrentInventoryPieChart";
import { OverviewCard } from "./OverviewCard";

export const CurrentInventoryEstimate = ({
  timestamp,
}: {
  timestamp: string;
}) => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const formatNumber = useFormatNumber();
  const { data, isFetching } = useGetInventoryEstimateAtTimestampQuery({
    tenantName: tenant,
    timestamp: timestamp,
    materialMetadataSetId: "default",
    materialPhysicsSetId: "default",
  });

  const totalInventory =
    data?.items.reduce((sum, item) => sum + (item.quantity ?? 0), 0) ?? 0;

  const unitsFormatter = useUnitsFormatter(false);

  const headerContent = (
    <Stack textAlign="right" sx={{ ml: "auto" }}>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {t("totalMaterialMass")}
      </Typography>
      {isFetching ? (
        <Skeleton variant="text" height={30} width={100} />
      ) : (
        <Typography
          variant="h4"
          sx={{ fontWeight: "fontWeightBold", color: "primary.main" }}
        >
          {formatNumber(0)(totalInventory)}
          {unitsFormatter("mass", false)}
        </Typography>
      )}
    </Stack>
  );

  return (
    <OverviewCard
      title={t("currentInventoryEstimate")}
      icon={InventoryIcon}
      headerContent={headerContent}
      buttonText={t("viewFullInventory")}
      buttonHref={`/${tenant}/dashboard/inventory`}
      delay={0.2}
    >
      <CurrentInventoryPieChart timestamp={timestamp} />
    </OverviewCard>
  );
};
